/** @format */

import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    TextField,
    Alert,
    InputAdornment,
    IconButton,
    Box,
    Typography,
    CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import { AppDispatch, RootState } from "../../../redux/store/store";
import { registerTeamMember } from "../../../redux/slices/businessAuthSlice";
import { CollectPrimaryButton } from "../styles";
import { colors } from "../../../utility/types/constant";
import { BusinessAuthShell } from "./BusinessAuthShell";

const BusinessTeamSignUp: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isRegistering, registerError } = useSelector(
        (s: RootState) => s.businessAuth,
    );

    // Pre-fill from the invite link (`?businessId=...&email=...`) so an invited
    // teammate only has to set their name + password.
    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        businessId: searchParams.get("businessId") || "",
        email: searchParams.get("email") || "",
        password: "",
        phone: "",
    });
    const [showPassword, setShowPassword] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);

    const update =
        (field: keyof typeof form) =>
            (e: React.ChangeEvent<HTMLInputElement>) =>
                setForm((f) => ({ ...f, [field]: e.target.value }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLocalError(null);

        if (form.password.length < 8) {
            setLocalError("Password must be at least 8 characters.");
            return;
        }

        const result = await dispatch(
            registerTeamMember({
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                businessId: form.businessId.trim(),
                email: form.email.trim().toLowerCase(),
                password: form.password,
                phone: form.phone.trim() || undefined,
            }),
        );

        if (registerTeamMember.fulfilled.match(result)) {
            navigate("/collect/verify");
        }
    };

    return (
        <BusinessAuthShell
            title="Join your team"
            subtitle="Use the business ID your admin shared, along with the email they invited."
            footer={
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Already have an account?{" "}
                    <Link
                        to="/collect/sign-in"
                        style={{ color: colors.primary, fontWeight: 700 }}
                    >
                        Sign in
                    </Link>
                </Typography>
            }
        >
            <form onSubmit={handleSubmit} noValidate>
                {(localError || registerError) && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {localError || registerError}
                    </Alert>
                )}

                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <TextField
                        label="First name"
                        value={form.firstName}
                        onChange={update("firstName")}
                        fullWidth
                        required
                        margin="dense"
                    />
                    <TextField
                        label="Last name"
                        value={form.lastName}
                        onChange={update("lastName")}
                        fullWidth
                        required
                        margin="dense"
                    />
                </Box>

                <TextField
                    label="Business ID"
                    value={form.businessId}
                    onChange={update("businessId")}
                    fullWidth
                    required
                    margin="dense"
                    helperText="Provided by your business admin."
                />

                <TextField
                    label="Work email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    fullWidth
                    required
                    margin="dense"
                    helperText="Must match the email your admin invited."
                />

                <TextField
                    label="Phone (optional)"
                    value={form.phone}
                    onChange={update("phone")}
                    fullWidth
                    margin="dense"
                />

                <TextField
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={update("password")}
                    fullWidth
                    required
                    margin="dense"
                    helperText="At least 8 characters."
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowPassword((v) => !v)}
                                    edge="end"
                                >
                                    {showPassword ? (
                                        <VisibilityOff />
                                    ) : (
                                        <Visibility />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <CollectPrimaryButton
                    type="submit"
                    fullWidth
                    disabled={isRegistering}
                    sx={{ mt: 2 }}
                >
                    {isRegistering ? (
                        <CircularProgress size={22} sx={{ color: "#fff" }} />
                    ) : (
                        "Create account"
                    )}
                </CollectPrimaryButton>
            </form>
        </BusinessAuthShell>
    );
};

export default BusinessTeamSignUp;
